// ประเภทพื้นที่ (biome) (docs/PLAN.md ข้อ 4.2)
// บนบกใช้ 14 biome ของ RESOLVE Ecoregions 2017 (CC-BY 4.0) + หินและธารน้ำแข็ง
// ในทะเลแบ่งตามความลึก: ทะเลชายฝั่ง (ไหล่ทวีป ลึกไม่ถึง 200 ม.) กับทะเลเปิด

import type { Habitat } from "../engine/types";

export type LandBiomeId =
  | "tropical_moist_forest"
  | "tropical_dry_forest"
  | "tropical_conifer_forest"
  | "temperate_broadleaf_forest"
  | "temperate_conifer_forest"
  | "boreal_forest"
  | "tropical_savanna"
  | "temperate_grassland"
  | "flooded_grassland"
  | "montane_grassland"
  | "tundra"
  | "mediterranean"
  | "desert"
  | "mangrove"
  | "rock_ice";

export type BiomeId = LandBiomeId | "coastal_sea" | "open_ocean";

/** เลขในกริดตรงกับ BIOME_NUM ของ RESOLVE (1–14) และ 15 = หินและธารน้ำแข็ง (0 = ไม่มี) */
export const LAND_BIOMES: readonly (LandBiomeId | null)[] = [
  null,
  "tropical_moist_forest",
  "tropical_dry_forest",
  "tropical_conifer_forest",
  "temperate_broadleaf_forest",
  "temperate_conifer_forest",
  "boreal_forest",
  "tropical_savanna",
  "temperate_grassland",
  "flooded_grassland",
  "montane_grassland",
  "tundra",
  "mediterranean",
  "desert",
  "mangrove",
  "rock_ice",
];

export const BIOME_TH: Readonly<Record<BiomeId, string>> = {
  tropical_moist_forest: "ป่าชื้นเขตร้อน",
  tropical_dry_forest: "ป่าแล้งเขตร้อน",
  tropical_conifer_forest: "ป่าสนเขตร้อน",
  temperate_broadleaf_forest: "ป่าผลัดใบเขตอบอุ่น",
  temperate_conifer_forest: "ป่าสนเขตอบอุ่น",
  boreal_forest: "ป่าไทกา",
  tropical_savanna: "ทุ่งหญ้าสะวันนา",
  temperate_grassland: "ทุ่งหญ้าเขตอบอุ่น",
  flooded_grassland: "ทุ่งหญ้าน้ำท่วมถึง",
  montane_grassland: "ทุ่งหญ้าบนภูเขาสูง",
  tundra: "ทุนดรา",
  mediterranean: "ป่าละเมาะแบบเมดิเตอร์เรเนียน",
  desert: "ทะเลทรายและพุ่มไม้แห้งแล้ง",
  mangrove: "ป่าชายเลน",
  rock_ice: "ธารน้ำแข็งและหิน",
  coastal_sea: "ทะเลชายฝั่ง",
  open_ocean: "ทะเลเปิด",
};

export const SHELF_DEPTH = 200; // ทะเลที่ตื้นกว่านี้นับเป็นทะเลชายฝั่ง (ไหล่ทวีป)

const FORESTS: readonly LandBiomeId[] = [
  "tropical_moist_forest", "tropical_dry_forest", "tropical_conifer_forest",
  "temperate_broadleaf_forest", "temperate_conifer_forest", "boreal_forest", "mediterranean", "mangrove",
];
const GRASSLANDS: readonly LandBiomeId[] = ["tropical_savanna", "temperate_grassland", "flooded_grassland", "montane_grassland", "mediterranean"];

// ถิ่นที่อยู่ของชนิด → biome ที่เข้ากันได้ (null = อยู่ได้ทุก biome เช่น น้ำจืด เมือง ถ้ำ)
const HABITAT_BIOMES: Readonly<Record<Habitat, readonly LandBiomeId[] | null>> = {
  forest: FORESTS,
  grassland: GRASSLANDS,
  desert: ["desert", "mediterranean", "tropical_savanna"],
  mountain: ["montane_grassland", "temperate_conifer_forest", "tropical_conifer_forest", "rock_ice", "tundra"],
  wetland: null,
  freshwater: null,
  sea: null,
  urban: null,
  cave: null,
};

/** biome บนบกที่ชนิดนี้อยู่ได้ (null = ได้ทุกแบบ) ใช้กรองถิ่นที่อยู่ที่ประมาณจากรายชื่อประเทศ */
export function fittingBiomes(habitat: readonly Habitat[]): ReadonlySet<LandBiomeId> | null {
  const out = new Set<LandBiomeId>();
  for (const h of habitat) {
    if (h === "sea") continue;
    const list = HABITAT_BIOMES[h];
    if (!list) return null;
    list.forEach((b) => out.add(b));
  }
  return out.size ? out : null;
}
